import { buildTransferableSkillsLexicon, clean, uniqCI } from './transferableSkillsLexicon';

const stringsFrom = (value) => {
  if (Array.isArray(value)) return value.flatMap(stringsFrom);
  if (value && typeof value === 'object') return Object.values(value).flatMap(stringsFrom);
  return clean(value) ? [clean(value)] : [];
};

const normalizeWorkEvidence = (entry = {}, index) => ({
  index,
  jobTitle: clean(entry.jobTitle),
  employer: clean(entry.employer),
  employerAddress: clean(entry.employerAddress),
  startDate: clean(entry.startDate),
  endDate: clean(entry.endDate),
  present: !!entry.present,
  hoursPerWeek: clean(entry.hoursPerWeek),
  supervisor: clean(entry.supervisor),
  supervisorContact: clean(entry.supervisorContact),
  canContactSupervisor: clean(entry.canContactSupervisor),
  responsibilities: uniqCI([
    entry.responsibilities,
    ...(entry.responsibilitiesBullets || []),
    ...(entry.enhancedResponsibilities || []),
  ]),
  accomplishments: uniqCI([
    entry.keyAccomplishments,
    ...(entry.accomplishmentsBullets || []),
    ...(entry.enhancedAccomplishments || []),
  ]),
  anchors: uniqCI([
    entry.jobTitle,
    entry.employer,
    entry.employerAddress,
    entry.responsibilities,
    entry.keyAccomplishments,
  ]),
});

const normalizeEducationEvidence = (entry = {}, index) => ({
  index,
  institution: clean(entry.institution || entry.institutionName),
  degree: clean(entry.degree || entry.degreeName || entry.degreeType),
  major: clean(entry.major),
  minor: clean(entry.minor),
  startDate: clean(entry.startDate),
  endDate: clean(entry.endDate),
  graduationYear: clean(entry.graduationYear),
  relevantCourses: uniqCI([
    ...(entry.relevantCourses || []),
    ...(entry.relevantCoursesBullets || []),
    ...(entry.courses || []),
  ]),
  projects: uniqCI(stringsFrom([
    ...(entry.enhancedProjects || []),
    ...(entry.significantProjects || []),
  ])),
});

export const isEducationEntryVisible = (entry) => entry?.showOnResume !== false;

const normalizeVolunteerEvidence = (entry = {}, index) => ({
  index,
  organization: clean(entry.organization),
  role: clean(entry.role),
  startDate: clean(entry.startDate),
  endDate: clean(entry.endDate),
  present: !!entry.present,
  hoursPerWeek: clean(entry.hoursPerWeek),
  responsibilities: clean(entry.responsibilities),
});

export const buildOneShotResumeLexicon = (formData = {}, jobAnalysis = {}) => {
  const base = buildTransferableSkillsLexicon(formData);
  const visibleWorkHistory = (Array.isArray(formData.workHistory) ? formData.workHistory : [])
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => entry?.showOnResume !== false)
    .map(({ entry, index }) => normalizeWorkEvidence(entry, index));

  const education = (Array.isArray(formData.educationHistory) ? formData.educationHistory : [])
    .filter(isEducationEntryVisible)
    .map(normalizeEducationEvidence);

  const volunteer = (Array.isArray(formData.volunteerWork) ? formData.volunteerWork : [])
    .map(normalizeVolunteerEvidence);

  const certifications = (Array.isArray(formData.certifications) ? formData.certifications : [])
    .map((entry = {}, index) => ({
      index,
      certificationName: clean(entry.certificationName),
      certificationAuthority: clean(entry.certificationAuthority),
      issueDate: clean(entry.issueDate),
      expiryDate: clean(entry.expiryDate),
    }));

  const military = {
    status: clean(formData.militaryInfo?.militaryStatus),
    branch: clean(formData.militaryInfo?.militaryBranch),
    rank: clean(formData.militaryInfo?.militaryRank),
    duration: clean(formData.militaryInfo?.militaryDuration),
    occupation: clean(formData.militaryInfo?.militaryOccupation),
    location: clean(formData.militaryInfo?.militaryLocation),
  };

  const targetKeywords = uniqCI([
    ...(jobAnalysis.keywords || []),
    ...(jobAnalysis.jobKeywords || []),
    ...(formData.selectedJobKeywords || []),
    ...(formData.jobKeywords || []),
    ...(formData.keywords || []),
  ]);

  const targetContext = {
    specializedExperience: clean(
      jobAnalysis.experienceRequirements
      || jobAnalysis.SpecializedExperience
      || formData.experienceRequirements
      || formData.specializedExperience,
    ),
    competencies: stringsFrom(jobAnalysis.softSkills || jobAnalysis.Competencies || formData.softSkills || formData.competencies),
    knowledge: stringsFrom(jobAnalysis.requiredSkills || jobAnalysis.Knowledge || formData.requiredSkills || formData.knowledge),
    educationRequirements: clean(jobAnalysis.educationRequirements || formData.educationRequirements),
    jobSummary: clean(jobAnalysis.jobSummary || formData.jobSummary),
    keywords: targetKeywords,
  };

  const evidenceSignals = uniqCI([
    ...visibleWorkHistory.flatMap((entry) => [
      entry.jobTitle,
      entry.employer,
      ...entry.responsibilities,
      ...entry.accomplishments,
    ]),
    ...education.flatMap((entry) => [
      entry.institution,
      entry.degree,
      entry.major,
      entry.minor,
      ...entry.relevantCourses,
      ...entry.projects,
    ]),
    ...volunteer.flatMap((entry) => [
      entry.organization,
      entry.role,
      entry.responsibilities,
    ]),
    ...base.military,
    ...base.credentials,
    ...base.additionalExperience,
    ...base.existingSkills,
  ]);

  return {
    ...base,
    evidenceSignals,
    workHistory: visibleWorkHistory,
    education,
    volunteerWork: volunteer,
    certifications,
    training: uniqCI(stringsFrom(formData.training)),
    organizations: uniqCI(stringsFrom(formData.organizations)),
    languages: uniqCI(stringsFrom(formData.languages)),
    awards: uniqCI(stringsFrom(formData.awards)),
    interests: uniqCI(stringsFrom(formData.interests)),
    military,
    existingSkills: uniqCI([
      ...stringsFrom(formData.skills),
      ...stringsFrom(formData.selectedSkills),
    ]),
    targetContext,
    groundingPolicy: {
      candidateEvidenceOnly: true,
      targetRequirementsAreContextOnly: true,
      doNotInventMissingFederalDetails: true,
    },
  };
};

export { clean, uniqCI };
